import { useEffect, useMemo, useRef, useState } from 'react'
import { api, type SearchHit } from '../api'
import { Icon, type IconName } from './Icon'
import { useDialog } from '../useDialog'

/**
 * Cmd+K. In a tool whose left rail will eventually hold a thousand pages, typing
 * three letters has to beat scrolling — so this is the primary way to move
 * around, and the sidebar becomes the way to understand structure.
 */
export interface Command {
  id: string
  label: string
  hint?: string
  group: string
  icon: IconName
  keywords?: string
  run: () => void
  disabled?: boolean
}

export function CommandPalette({
  open,
  commands,
  onClose,
  branch,
  onOpenHit,
  emptyAction,
}: {
  open: boolean
  commands: Command[]
  onClose: () => void
  /** The draft being searched, so hits come from what the writer is looking at. */
  branch?: string | null
  onOpenHit?: (hit: SearchHit) => void
  /**
   * The one act offered when a search finds nothing.
   *
   * "Nothing matches" is the truth and a dead end: the page somebody looked
   * for does not exist, and the obvious answer is to make it. Given by the
   * caller because only it knows whether that is possible right now — and it
   * is left out entirely for somebody who may not write, since a control that
   * would only refuse is not offered.
   */
  emptyAction?: { label: string; icon: IconName; run: () => void }
}) {
  const panel = useDialog<HTMLDivElement>(open, onClose)
  const [query, setQuery] = useState('')
  const [index, setIndex] = useState(0)
  const listRef = useRef<HTMLDivElement>(null)

  const results = useMemo(() => rank(commands, query), [commands, query])

  /**
   * Content hits, debounced. The commands filter instantly because they are in
   * memory; the pages need a request, and firing one per keystroke against a
   * GitHub-backed workspace is a way to feel slow. 160ms is under the gap
   * between keystrokes while typing a word.
   */
  const [hits, setHits] = useState<SearchHit[]>([])
  /**
   * What the read of the pages is doing, which is not the same question as how
   * many hits came back.
   *
   * `.catch(() => setHits([]))` stood here, and it turned every failed search
   * into an empty one: a request refused, a session that had expired, the
   * index still building — all of them arrived on the screen as "Nothing
   * matches", which is a claim about the documentation rather than about the
   * request. Somebody is then told their page does not exist, and offered a
   * button to write it a second time.
   *
   * `reading` matters for the same reason. The commands are matched in memory
   * and are instant; the pages take a request, so for the first fifth of a
   * second there are no hits — and saying "nothing matches" then is asserting
   * the answer to a question nobody has answered yet.
   */
  const [reading, setReading] = useState(false)
  const [searchFailed, setSearchFailed] = useState<string | null>(null)
  useEffect(() => {
    if (!open || query.trim().length < 2 || !onOpenHit) {
      setHits([])
      setReading(false)
      setSearchFailed(null)
      return
    }
    setReading(true)
    const t = window.setTimeout(() => {
      void api.search(query.trim(), branch ?? undefined).then(
        (r) => {
          setHits(r.hits)
          setSearchFailed(null)
          setReading(false)
        },
        (e) => {
          setHits([])
          setSearchFailed(e instanceof Error ? e.message : String(e))
          setReading(false)
        },
      )
    }, 160)
    return () => window.clearTimeout(t)
  }, [query, open, branch, onOpenHit])

  useEffect(() => {
    if (open) {
      setQuery('')
      setIndex(0)
    }
  }, [open])

  useEffect(() => {
    setIndex(0)
  }, [query])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') return onClose()
      if (e.key === 'ArrowDown' || (e.key === 'n' && e.ctrlKey)) {
        e.preventDefault()
        setIndex((i) => Math.min(i + 1, results.length + hits.length - 1))
      } else if (e.key === 'ArrowUp' || (e.key === 'p' && e.ctrlKey)) {
        e.preventDefault()
        setIndex((i) => Math.max(i - 1, 0))
      } else if (e.key === 'Enter') {
        e.preventDefault()
        const chosen = results[index]
        if (chosen && !chosen.disabled) {
          chosen.run()
          onClose()
        } else if (index >= results.length) {
          const hit = hits[index - results.length]
          if (hit && onOpenHit) {
            onOpenHit(hit)
            onClose()
          }
        }
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, results, hits, index, onClose, onOpenHit])

  useEffect(() => {
    listRef.current?.querySelector('.is-active')?.scrollIntoView({ block: 'nearest' })
  }, [index])

  if (!open) return null

  const groups = new Map<string, Command[]>()
  for (const c of results) groups.set(c.group, [...(groups.get(c.group) ?? []), c])

  return (
    <div className="scrim" onMouseDown={onClose}>
      <div
        className="palette"
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="palette__search">
          <Icon name="search" size={16} />
          <input
            autoFocus
            className="palette__input"
            placeholder="Go to a page, or run a command…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <kbd>esc</kbd>
        </div>

        <div className="palette__list" ref={listRef}>
          {searchFailed !== null && (
            <p className="panel__error" role="alert">
              Could not search the pages: {searchFailed}. The commands are matched in this browser,
              so they are unaffected.
            </p>
          )}
          {results.length === 0 && hits.length === 0 && searchFailed === null && reading && (
            /* Its own class, not `.palette__none`: that one is a CLAIM — we
               looked at every page and there is nothing — and two gates read
               it as exactly that claim. */
            <p className="palette__reading">Searching the pages…</p>
          )}
          {results.length === 0 && hits.length === 0 && !reading && searchFailed === null && (
            <div className="empty palette__none">
              <p className="empty__text">Nothing matches “{query}”.</p>
              {emptyAction && (
                <button
                  className="btn btn--tinted btn--sm"
                  onMouseDown={(e) => {
                    // Same as the command rows: act on mousedown, before the
                    // input's blur can move the ground under the click.
                    e.preventDefault()
                    emptyAction.run()
                    onClose()
                  }}
                >
                  <Icon name={emptyAction.icon} size={13} />
                  {emptyAction.label}
                </button>
              )}
            </div>
          )}
          {[...groups.entries()].map(([group, items]) => (
            <div key={group}>
              <div className="label palette__group">{group}</div>
              {items.map((c) => (
                <button
                  key={c.id}
                  className={`menu-item${results.indexOf(c) === index ? ' is-active' : ''}`}
                  disabled={c.disabled}
                  onMouseEnter={() => setIndex(results.indexOf(c))}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    c.run()
                    onClose()
                  }}
                >
                  <span className="menu-item__icon">
                    <Icon name={c.icon} size={15} />
                  </span>
                  <span className="menu-item__body">
                    <span className="menu-item__label truncate">{c.label}</span>
                    {c.hint && <span className="menu-item__hint truncate">{c.hint}</span>}
                  </span>
                </button>
              ))}
            </div>
          ))}

          {hits.length > 0 && (
            <div>
              <div className="label palette__group">In pages</div>
              {hits.map((hit, i) => (
                <button
                  key={`${hit.path}-${i}`}
                  className={`menu-item${results.length + i === index ? ' is-active' : ''}`}
                  onMouseEnter={() => setIndex(results.length + i)}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    onOpenHit?.(hit)
                    onClose()
                  }}
                >
                  <span className="menu-item__icon">
                    <Icon name="page" size={15} />
                  </span>
                  <span className="menu-item__body">
                    <span className="menu-item__label truncate">
                      {hit.title}
                      {hit.heading && <span className="palette__anchor"> › {hit.heading}</span>}
                    </span>
                    <span className="menu-item__hint truncate">
                      {hit.snippet.slice(0, hit.matchStart)}
                      <mark>
                        {hit.snippet.slice(hit.matchStart, hit.matchStart + hit.matchLength)}
                      </mark>
                      {hit.snippet.slice(hit.matchStart + hit.matchLength)}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="palette__foot">
          <span>
            <kbd>↑</kbd>
            <kbd>↓</kbd> move
          </span>
          <span>
            <kbd>↵</kbd> open
          </span>
          <span>
            <kbd>?</kbd> all shortcuts
          </span>
        </div>
      </div>
    </div>
  )
}

/**
 * Subsequence matching, the behaviour people expect from a palette: "gsi"
 * should find "getting-started/intro". Earlier and tighter matches rank higher,
 * so an exact prefix always wins over a scattered one.
 */
function rank(commands: Command[], query: string): Command[] {
  const q = query.trim().toLowerCase()
  if (!q) return commands
  const scored: { c: Command; score: number }[] = []

  for (const c of commands) {
    const haystack = `${c.label} ${c.hint ?? ''} ${c.keywords ?? ''}`.toLowerCase()
    let score = 0
    let at = -1
    let ok = true
    let gaps = 0

    for (const ch of q) {
      const next = haystack.indexOf(ch, at + 1)
      if (next === -1) {
        ok = false
        break
      }
      gaps += next - at - 1
      at = next
    }
    if (!ok) continue

    score = 1000 - gaps * 4 - at
    if (haystack.startsWith(q)) score += 500
    if (c.label.toLowerCase().includes(q)) score += 200
    scored.push({ c, score })
  }

  return scored.sort((a, b) => b.score - a.score).map((s) => s.c)
}
